import Reveal from "../components/Reveal";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { SIGN_UP_URL, SUPPORT_EMAIL } from "../config";
import { trackCta } from "../hooks/useMarketingAnalytics";

// Verified from the plans table via /api/plans — do not edit without checking there.
const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$29",
    per: "/month",
    tag: null,
    features: [
      "2 team members",
      "Up to 50 clients",
      "1 GB secure file storage",
      "Client intake & referrals",
      "Task management & shared calendar",
      "Team assignments & activity log",
      "Standard reports & notifications",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: "$79",
    per: "/month",
    tag: "Most popular",
    features: [
      "Everything in Starter",
      "10 team members",
      "Up to 500 clients",
      "10 GB secure file storage",
      "Advanced intake, calendar & activity",
      "Advanced reports & full notifications",
      "AI assistant · 100 requests / month",
      "Priority support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: "$149",
    per: "/month",
    tag: null,
    features: [
      "Everything in Professional",
      "25 team members",
      "Up to 2,000 clients",
      "50 GB secure file storage",
      "AI assistant · 500 requests / month",
      "Advanced reporting & custom branding",
      "Multiple locations & API integrations",
    ],
  },
];

export default function PricingPreview({ compact = false }) {
  return (
    <section
      data-testid="pricing-preview"
      className={compact ? "py-16" : "py-24 lg:py-32"}
      style={{ background: "#FAF7F2" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl mx-auto text-center mb-14">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#E8712C] mb-3">
            Pricing
          </div>
          <h2 className="font-display font-extrabold text-[32px] sm:text-[44px] leading-[1.08] tracking-[-0.02em] text-[#1A2A3A] mb-4">
            Simple pricing. No surprise fees.
          </h2>
          <p className="text-[16px] text-[#57534E] leading-relaxed">
            Every plan includes clients, bookings, tasks, forms, contracts,
            invoices and calendar. Choose the size that fits your practice.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {PLANS.map((p, i) => {
            const featured = p.tag === "Most popular";
            return (
              <Reveal key={p.id} delay={i * 80}>
                <article
                  data-testid={`plan-card-${p.id}`}
                  className={`relative h-full rounded-3xl p-8 border transition-all ${
                    featured
                      ? "bg-[#334E68] text-white border-[#334E68] shadow-[0_25px_50px_-25px_rgba(51,78,104,0.45)]"
                      : "bg-white border-[#E8E2D9]"
                  }`}
                >
                  {p.tag && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex px-3 py-1 bg-[#E8712C] text-white text-[11px] font-semibold uppercase tracking-wider rounded-full">
                      {p.tag}
                    </div>
                  )}
                  <div className={`font-display font-bold text-[20px] tracking-[-0.01em] ${featured ? "" : "text-[#1A2A3A]"}`}>
                    {p.name}
                  </div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className={`font-display font-extrabold text-[44px] tracking-[-0.03em] ${featured ? "" : "text-[#1A2A3A]"}`}>
                      {p.price}
                    </span>
                    <span className={`text-[14px] ${featured ? "text-white/70" : "text-[#57534E]"}`}>
                      {p.per}
                    </span>
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-[14px] ${featured ? "text-white/85" : "text-[#1A2A3A]"}`}>
                        <Check
                          size={16}
                          strokeWidth={2}
                          className={`mt-[3px] flex-shrink-0 ${featured ? "text-[#F0B67F]" : "text-[#E8712C]"}`}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={SIGN_UP_URL}
                    onClick={() => trackCta("pricing_card_cta", { plan: p.id })}
                    className={`mt-8 inline-flex justify-center w-full px-5 py-3 rounded-full text-[14px] font-semibold transition-colors ${
                      featured
                        ? "bg-[#E8712C] hover:bg-[#C55A1F] text-white"
                        : "bg-[#334E68] hover:bg-[#1B2C3F] text-white"
                    }`}
                  >
                    Get started
                  </a>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={280} className="mt-10">
          <div className="max-w-3xl mx-auto text-center rounded-2xl bg-white border border-[#E8E2D9] p-6 sm:p-7">
            <div className="font-display font-bold text-[17px] text-[#1A2A3A] mb-2">
              Need more? Talk to us.
            </div>
            <p className="text-[14px] text-[#57534E] leading-relaxed">
              More users, more clients, larger storage, or a custom setup for
              your team? <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#E8712C] font-medium hover:underline">Get in touch</a>{" "}
              and we'll build a plan around you.
            </p>
          </div>
        </Reveal>

        {compact && (
          <div className="mt-8 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#E8712C] hover:underline"
            >
              Compare all plans →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
